import {
  Ban,
  CalendarCheck,
  CheckCircle2,
  Circle,
  Flag,
  Lightbulb,
  PackageCheck,
  Palette,
  PhoneMissed,
  RefreshCw,
  ShieldCheck,
  Wrench,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import type { LeadPrioridade, LeadStage, ProjectStage } from "@/lib/types";
import { LEAD_PRIORIDADE_LABELS, LEAD_STAGE_LABELS, PROJECT_STAGE_LABELS } from "@/lib/types";

// Cor NUNCA e o unico sinalizador (WCAG 1.4.1): todo badge combina
// cor + icone (lucide-react) + texto.

export type Tone = "neutral" | "success" | "warning" | "danger" | "primary";

/** Tom semantico -> variavel de cor do tema. Reaproveitado fora dos badges
 * (ex.: borda superior da coluna do Kanban) pra nao duplicar a paleta de
 * tons em mais de um lugar. */
export const TONE_VAR: Record<Tone, string> = {
  neutral: "var(--border-strong)",
  primary: "var(--primary)",
  warning: "var(--warning)",
  danger: "var(--danger)",
  success: "var(--success)",
};

// Exportado: reaproveitado pelo Kanban (borda superior da coluna colorida
// pela etapa — mesma fonte de verdade do StageBadge, nunca duas mapas
// etapa->tom divergentes).
export const STAGE_TONE: Record<LeadStage, Tone> = {
  novo: "neutral",
  tentando_contato: "primary",
  nao_atendeu: "warning",
  recusou: "danger",
  reuniao_agendada: "primary",
  reuniao_realizada: "success",
  cliente_fechado: "success",
  nao_fechou: "danger",
};

// cliente_fechado reusa CheckCircle2 (mesmo icone de "sucesso" usado no
// stat card e no toast) em vez de introduzir um icone novo tipo trofeu.
const STAGE_ICON: Record<LeadStage, LucideIcon> = {
  novo: Circle,
  tentando_contato: RefreshCw,
  nao_atendeu: PhoneMissed,
  recusou: XCircle,
  reuniao_agendada: CalendarCheck,
  reuniao_realizada: CheckCircle2,
  cliente_fechado: CheckCircle2,
  nao_fechou: XCircle,
};

export function StageBadge({ etapa }: { etapa: LeadStage }) {
  const Icon = STAGE_ICON[etapa];
  return (
    <span className={`badge badge--${STAGE_TONE[etapa]}`}>
      <Icon className="icon" size={14} aria-hidden="true" />
      {LEAD_STAGE_LABELS[etapa]}
    </span>
  );
}

/** Dot pequeno + rotulo em texto (nunca so a cor). "Alta" tambem ganha o
 * icone Flag (dual-encoding) — media/baixa ficam so com o dot, sem icone
 * extra, por decisao de design. */
export function PrioridadeBadge({ prioridade }: { prioridade: LeadPrioridade }) {
  return (
    <span className={`priority priority--${prioridade}`}>
      <span className="priority__dot" aria-hidden="true" />
      {prioridade === "alta" && <Flag className="icon priority__flag" size={14} aria-hidden="true" />}
      {LEAD_PRIORIDADE_LABELS[prioridade]}
    </span>
  );
}

const PROJECT_STAGE_TONE: Record<ProjectStage, Tone> = {
  descoberta: "neutral",
  design: "primary",
  desenvolvimento: "primary",
  qa: "warning",
  entrega: "success",
  pos_venda: "success",
};

const PROJECT_STAGE_ICON: Record<ProjectStage, LucideIcon> = {
  descoberta: Lightbulb,
  design: Palette,
  desenvolvimento: Wrench,
  qa: ShieldCheck,
  entrega: PackageCheck,
  pos_venda: CheckCircle2,
};

export function ProjectStageBadge({ etapa }: { etapa: ProjectStage }) {
  const Icon = PROJECT_STAGE_ICON[etapa];
  return (
    <span className={`badge badge--${PROJECT_STAGE_TONE[etapa]}`}>
      <Icon className="icon" size={14} aria-hidden="true" />
      {PROJECT_STAGE_LABELS[etapa]}
    </span>
  );
}

export function NaoContatarBadge() {
  return (
    <span className="badge badge--danger">
      <Ban className="icon" size={14} aria-hidden="true" />
      Não contatar
    </span>
  );
}
